import { Injectable } from '@angular/core';
import { BookingService } from '@markant-platform/cartridge/data-access';
import { Observable } from 'rxjs';
import { User } from '@markant-platform/shared/user/model';
import { handleDownloadByObjectUrl, isBulkPrice } from '@markant-platform/shared/util-helpers';
import { BookingData, BookingDataWithUpgradeInfo, CartridgeVariant } from '@markant-platform/cartridge/model';
import { BookingAnalyticsEventData, TrackingService } from '@markant-platform/shared/util-tracking';
import { BookingWorkflowData } from '../booking/model/booking.model';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';

@Injectable({
  providedIn: 'root',
})
export class CartridgeBookingService {
  static ORGANIZATION_PRICE_REQUEST_URL = '/organization/booked-cartridges/price-requests/organization-administrator/';

  constructor(
    private bookingService: BookingService,
    private readonly trackingService: TrackingService,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  getFullUserName(user: User | undefined): string {
    if (user) {
      return `${user.firstname} ${user.lastname}`;
    }

    return '';
  }

  downloadLegalDocument(bookingId: string | undefined): void {
    if (!bookingId) {
      return;
    }

    this.bookingService.getLegalDocumentAsObjectUrlByBookingId(bookingId).subscribe({
      next: handleDownloadByObjectUrl,
    });
  }

  getBookingById(bookingId: string): Observable<BookingDataWithUpgradeInfo> {
    return this.bookingService.getBookingById(bookingId);
  }

  getBookingByVariantId(variantId: string): Observable<BookingData | undefined> {
    return this.bookingService.getLatestBookingByVariantId(variantId);
  }

  //Track the booking process for services.
  pushTrackingEventData(stepName: string, result: BookingWorkflowData, actionName: string): void {
    const eventData: BookingAnalyticsEventData = {
      step: stepName,
      service: result.cartridge.name,
      price: this.getVariantPrice(result.variant),
      currency: result.variant.price?.currency ?? '-',
      pricing_model: this.getPriceModel(result.variant),
      company_name: result.organization.name,
      user_id: this.authenticatedUserService.userId as string,
      service_variant_id: result.variant.variantId,
      service_variant_name: result.variant.name,
      target_audience: result.organization.industry ?? '-',
      setup_price: result.variant.setUpCost ? result.variant.setUpCost.centAmount / 100 : 0,
      action: actionName,
    };

    this.trackingService.pushEventObjectToDataLayer('Service booking', eventData);
  }

  getVariantPrice(data: CartridgeVariant): number {
    const priceData = data.price ? data.price.centAmount / 100 : 0;
    return isBulkPrice(data.price) ? 0 : priceData;
  }

  getPriceModel(data: CartridgeVariant): string {
    return data.price ? `${data.price.priceModel} - ${data.price.billingPeriod}` : '-';
  }

  blockUnblockService(bookingId: string, block: boolean): Observable<void> {
    return this.bookingService.requestBlockService(bookingId, block);
  }

  suggestUpgradeService(bookingId: string): Observable<void> {
    return this.bookingService.requestSuggestUpgradeService(bookingId);
  }
}
